import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UITextElement, MyTextAlign, MyTextVerticalAlign } from "../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { LinkedSocialProvider } from "./account_providers_preview_state.js";
import { ImgID } from "../../image_loader/img_id.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";

export class FailedDisconnectSocialMediaDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.FAILED_DISCONNECT_SOCIAL_MEDIA_DIALOG;
  private bg!: RoundedRectangle;
  private content_bg!: RoundedRectangle;
  private current_social_media: string;

  constructor( private current_provider: LinkedSocialProvider = LinkedSocialProvider.Facebook )
  {
    const body_w = LayoutScaler.widthFromDesignAdaptive( 990 );
    const body_h = LayoutScaler.heightFromDesign( 680 );

    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );
    this.current_social_media = this.getCurrentSocialMediaLabel();

    this.createElements();
  }

  private createElements(): void
  {
    this.createBackground();
    this.createTitle();
    this.createOkButton();
    this.createCloseIcon();
  }

  private createTitle(): void
  {
    const label_y = LayoutScaler.yFromDesign( 49 );
    const label_h = LayoutScaler.heightFromDesign( 68 );
    const label_w = LayoutScaler.widthFromDesign( 900 );
    const label_x = ( this.rect.getW() - label_w ) / 2;

    const label = new UITextElement(
      this.ctx,
      label_x,
      label_y,
      label_w,
      label_h,
      {
        localization_key: "FAILED_SOCIAL_MEDIA_DIALOG.TITLE",
        text_variable_params: [this.current_social_media],
        text_color: "#DE934F",
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 700,
        auto_wrap: false,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    this.addChild( label );
  }

  private createBackground(): void
  {
    this.bg = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH(),
      LayoutScaler.cornerRadiusFromDesign( 30 ),
      "#FFFFFF"
    );

    this.addChild( this.bg );

    const circle_w = LayoutScaler.widthFromDesign( 237 );
    const circle_h = LayoutScaler.heightFromDesign( 234 );
    const circle_x = this.rect.getW2() - circle_w / 2;
    const circle_y = LayoutScaler.yFromDesign( 136 );

    const circle = new RoundedRectangle(
      this.ctx,
      circle_x,
      -circle_y,
      circle_w,
      circle_h,
      LayoutScaler.cornerRadiusFromDesign( 117 ),
      "#FFFFFF"
    );
    this.addChild( circle );

    const icon_size = LayoutScaler.widthFromDesign( 137 );
    const icon_x = ( circle_w - icon_size ) / 2;
    const icon_y = LayoutScaler.yFromDesign( 46 );

    const social_media_logo = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      this.getCurrentSocialMediaIcon()
    );
    circle.addChild( social_media_logo, true );

    const content_x = LayoutScaler.xFromDesign( 21 );
    const content_y = LayoutScaler.yFromDesign( 134 );
    const content_w = LayoutScaler.widthFromDesign( 948 );
    const content_h = LayoutScaler.heightFromDesign( 343 );

    this.content_bg = new RoundedRectangle(
      this.ctx,
      content_x,
      content_y,
      content_w,
      content_h,
      0,
      "#FFF3E8"
    );

    const label = new UITextElement(
      this.ctx,
      0,
      0,
      content_w,
      content_h,
      {
        localization_key: "FAILED_SOCIAL_MEDIA_DIALOG.WARNING",
        text_color: "#A85F1E",
        auto_wrap: true,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 700
      }
    );
    this.content_bg.addChild( label );
    this.addChild( this.content_bg );
  }

  private createOkButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 442 );
    const btn_h = LayoutScaler.heightFromDesign( 112 );
    const btn_x = this.rect.getW2() - btn_w / 2;
    const btn_y = LayoutScaler.yFromDesign( 524 );

    const btn = new RoundedRectangle(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      LayoutScaler.cornerRadiusFromDesign( 56 ),
      "#12B2E2",
      undefined,
      true
    );

    btn.setOnClick( () =>
    {
      DialogManager.closeDialog();
    } );

    btn.addChild( new UITextElement(
      this.ctx,
      0,
      0,
      btn_w,
      btn_h,
      {
        localization_key: "OK",
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.rubik,
        font_size: LayoutScaler.textSizeFromDesign( 44 ),
        font_weight: 700,
        ...TextStyleScheme.buttonShadow(),
        outline_color: ColorScheme.flat_light_blue_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    ), true );

    this.addChild( btn );
  }

  private createCloseIcon(): void
  {
    const icon_size = LayoutScaler.widthFromDesign( 85 );
    const icon_x = this.rect.getW() - icon_size - LayoutScaler.widthFromDesign( 7 );
    const icon_y = LayoutScaler.yFromDesign( 7 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_size,
      icon_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    icon.setOnClick( () => { DialogManager.closeDialog(); } );
    this.addChild( icon );
  }

  private getCurrentSocialMediaLabel(): string
  {
    switch ( this.current_provider )
    {
      case LinkedSocialProvider.Google:   return "Google";
      case LinkedSocialProvider.Apple:    return "Apple";
      case LinkedSocialProvider.Facebook:
      default:                            return "Facebook";
    }
  }

  private getCurrentSocialMediaIcon(): ImgID
  {
    switch ( this.current_provider )
    {
      case LinkedSocialProvider.Google:   return ImgID.GoogleIcon;
      case LinkedSocialProvider.Apple:    return ImgID.AppleIcon;
      case LinkedSocialProvider.Facebook:
      default:                            return ImgID.FacebookIcon;
    }
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "FailedDisconnectSocialMediaDialog";
  }
}
